import React, { useEffect, useMemo, useState } from "react";
import "./App.css";

import Main from "./components/main.js";
import AboutGame from "./components/aboutGame.js";
import Characters from "./components/characters";
import FightingStyles from "./components/fightingStyles";
import Fightsystem from "./components/fightSystem";
import StoryVideo from "./components/storyVideo";
import Materials from "./components/materials";
import DevNews from "./components/devNews";
import "./index.css";
import { translations } from "./siteText";

function App() {
    const [language, setLanguage] = useState(() => localStorage.getItem("site-language") || "en");

    useEffect(() => {
        let timer = null;
        let anim = false;

        const SNAP_PX = 90;
        const RELEASE_PX = 180;
        const IDLE_MS = 260;
        const COOLDOWN_MS = 650;

        let lastInputTs = 0;
        let lastSnapTop = null;

        const markInput = () => {
            lastInputTs = performance.now();
        };

        const getSections = () =>
            Array.from(document.querySelectorAll(".page-section"));

        const chooseSectionByViewportTop = (sections) => {
            const y = window.scrollY;
            let best = null;
            let bestDist = Infinity;

            for (const s of sections) {
                const top = s.offsetTop;
                const dist = Math.abs(top - y);
                if (dist < bestDist) {
                    bestDist = dist;
                    best = s;
                }
            }
            return { best, bestDist, y };
        };

        const onScroll = () => {
            if (anim) return;

            if (timer) clearTimeout(timer);

            timer = setTimeout(() => {
                const now = performance.now();
                if (now - lastInputTs < IDLE_MS) return;

                const sections = getSections();
                if (!sections.length) return;

                const { best, bestDist, y } = chooseSectionByViewportTop(sections);
                if (!best) return;

                const top = best.offsetTop;

                if (lastSnapTop !== null && Math.abs(y - lastSnapTop) < RELEASE_PX) {
                    return;
                }

                if (bestDist <= SNAP_PX) {
                    anim = true;
                    lastSnapTop = top;
                    best.scrollIntoView({ behavior: "smooth", block: "start" });

                    setTimeout(() => {
                        anim = false;
                    }, COOLDOWN_MS);
                }
            }, IDLE_MS);
        };

        const onWheel = () => markInput();
        const onTouchStart = () => markInput();
        const onTouchMove = () => markInput();
        const onPointerDown = () => markInput();

        const onKeyDown = (e) => {
            const keys = ["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "];
            if (keys.includes(e.key)) markInput();
        };

        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("wheel", onWheel, { passive: true });
        window.addEventListener("touchstart", onTouchStart, { passive: true });
        window.addEventListener("touchmove", onTouchMove, { passive: true });
        window.addEventListener("pointerdown", onPointerDown, { passive: true });
        window.addEventListener("keydown", onKeyDown);

        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("wheel", onWheel);
            window.removeEventListener("touchstart", onTouchStart);
            window.removeEventListener("touchmove", onTouchMove);
            window.removeEventListener("pointerdown", onPointerDown);
            window.removeEventListener("keydown", onKeyDown);
            if (timer) clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        localStorage.setItem("site-language", language);
        document.documentElement.lang = language === "ru" ? "ru" : "en";
    }, [language]);

    const t = useMemo(() => translations[language] || translations.en, [language]);

    return (
        <main className="page">
            <div className="lang-switch" role="group" aria-label={t.languageLabel}>
                <button
                    type="button"
                    className={`lang-switch-btn ${language === "en" ? "is-active" : ""}`}
                    onClick={() => setLanguage("en")}
                    aria-pressed={language === "en"}
                >
                    EN
                </button>
                <button
                    type="button"
                    className={`lang-switch-btn ${language === "ru" ? "is-active" : ""}`}
                    onClick={() => setLanguage("ru")}
                    aria-pressed={language === "ru"}
                >
                    RU
                </button>
            </div>

            <section className="page-section" id="main"><Main t={t} /></section>
            <section className="page-section" id="about"><AboutGame t={t} /></section>
            <section className="page-section" id="characters"><Characters t={t} /></section>
            <section className="page-section" id="styles"><FightingStyles t={t} /></section>
            <section className="page-section" id="fight"><Fightsystem t={t} /></section>
            <section className="page-section" id="story"><StoryVideo t={t} /></section>
            <section className="page-section" id="screens"><Materials t={t} /></section>
            <section className="page-section" id="beta"><DevNews t={t} /></section>
        </main>
    );
}

export default App;
